import { Capacitor } from '@capacitor/core';

/**
 * CrossViewer가 지원하는 파일 확장자 목록
 */
const VIEWER_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf', 'hwp', 'hwpx'];

/**
 * 파일명 또는 URL에서 확장자를 추출하여 CrossViewer 지원 여부를 반환
 */
export function isViewerSupported(filenameOrUrl: string): boolean {
  const cleanPath = filenameOrUrl.split('?')[0].split('#')[0];
  const ext = cleanPath.split('.').pop()?.toLowerCase() ?? '';
  return VIEWER_EXTENSIONS.includes(ext);
}

/**
 * 앱 내부 뷰어 모달(iframe)에서 브라우저 기본 렌더러로 바로 보여줄 수 있는 텍스트/데이터 확장자.
 *
 * CrossViewer(오피스/PDF/한글)는 지원하지 않지만 브라우저가 인라인으로 렌더하는 것들.
 * iframe 안에서 렌더되므로 웹/네이티브(webview) 양쪽에서 앱을 벗어나지 않고 볼 수 있다.
 * (html/svg 등 스크립트 실행 가능 포맷은 안전상 제외 → 브라우저 탭 또는 다운로드)
 */
const INLINE_VIEWABLE_EXTENSIONS = ['txt', 'csv', 'json', 'xml', 'log', 'md', 'markdown', 'yml', 'yaml', 'tsv', 'ini', 'conf'];

/**
 * 파일명 또는 URL에서 확장자를 추출하여 "앱 내부 뷰어 모달(iframe) 렌더 가능" 여부를 반환.
 */
export function isInlineViewable(filenameOrUrl: string): boolean {
  const cleanPath = filenameOrUrl.split('?')[0].split('#')[0];
  const ext = cleanPath.split('.').pop()?.toLowerCase() ?? '';
  return INLINE_VIEWABLE_EXTENSIONS.includes(ext);
}

/**
 * 앱 내부 뷰어로는 못 열지만 브라우저 새 탭에서 열면 브라우저가 렌더/재생하는 확장자.
 * (오디오, html 등) zip·exe 등 무조건 다운로드되는 형식은 제외 → 다운로드.
 * 웹 전용 판정 — 네이티브 앱에서는 새 탭이 어려우므로 호출부에서 플랫폼을 함께 검사해 다운로드로 폴백한다.
 */
const BROWSER_TAB_EXTENSIONS = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'html', 'htm'];

/**
 * 파일명 또는 URL에서 확장자를 추출하여 "브라우저 새 탭 렌더/재생 가능" 여부를 반환.
 */
export function isBrowserTabViewable(filenameOrUrl: string): boolean {
  const cleanPath = filenameOrUrl.split('?')[0].split('#')[0];
  const ext = cleanPath.split('.').pop()?.toLowerCase() ?? '';
  return BROWSER_TAB_EXTENSIONS.includes(ext);
}

/**
 * NEXTPOD 언어코드 → CrossViewer 언어코드 매핑
 */
const LANG_MAP: Record<string, string> = {
  ko: 'kor', en: 'enu', ja: 'jpn',
  zh: 'chs', 'zh-TW': 'cht', 'zh-CN': 'chs',
  vi: 'vit', id: 'ind', fr: 'fra',
};

function getViewerLang(): string {
  try {
    const lang = (window as any).__NEXTPOD_LANG__ || navigator.language?.split('-')[0] || 'en';
    return LANG_MAP[lang] || LANG_MAP[lang.split('-')[0]] || 'enu';
  } catch {
    return 'enu';
  }
}

/**
 * CrossViewer URL을 생성 (사용자 언어 포함)
 * 로컬 경로 (웹 브라우저용)
 */
export function getViewerUrl(fileUrl: string): string {
  const lang = getViewerLang();
  return `/viewer/index.html?filePath=${encodeURIComponent(fileUrl)}&lang=${lang}`;
}

/**
 * Firebase Hosting의 CrossViewer URL 생성 (네이티브 앱용)
 * capacitor:// 스킴에서는 SharedArrayBuffer가 지원되지 않으므로
 * https:// 도메인의 뷰어를 SFSafariViewController로 열어서 사용
 */
function getHostedViewerUrl(fileUrl: string): string {
  const lang = getViewerLang();
  const domain = import.meta.env.VITE_MAIN_DOMAIN || 'devapp.nextpod.jp';
  return `https://${domain}/viewer/index.html?filePath=${encodeURIComponent(fileUrl)}&lang=${lang}`;
}

/**
 * CrossViewer를 새 탭에서 열기 (웹 브라우저용)
 */
export function openDocumentViewer(fileUrl: string): void {
  const viewerUrl = getViewerUrl(fileUrl);
  window.open(viewerUrl, '_blank', 'noopener');
}

interface DocumentViewerModalProps {
  url: string;
  title?: string;
  onClose: () => void;
}

/**
 * CrossViewer 문서 뷰어
 *
 * - 네이티브 앱(iOS/Android): Firebase Hosting의 CrossViewer를 인앱 브라우저로 열기
 *   (capacitor:// 스킴에서는 SharedArrayBuffer 미지원 → https:// 필요)
 * - 웹 브라우저: 새 탭으로 열기
 */
export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({ url, title, onClose }) => {

  // 네이티브 앱(iOS/Android) → Firebase Hosting의 CrossViewer를 인앱 브라우저로 열기
  if (Capacitor.isNativePlatform()) {
    const hostedUrl = getHostedViewerUrl(url);
    import('@capacitor/browser').then(({ Browser }) => {
      Browser.open({ url: hostedUrl, presentationStyle: 'popover' });
    });
    setTimeout(() => onClose(), 0);
    return null;
  }

  // 웹 브라우저 → 항상 새 탭으로 열기
  openDocumentViewer(url);
  setTimeout(() => onClose(), 0);
  return null;
};

